/*
 * The replay driver: a model, its wiring and a window of the past in; a trace
 * of what the logic would have decided out.
 *
 * Pure orchestration over three things it is handed rather than imports —
 * an engine (the sandbox session in the browser, an in-process host in a
 * test), a history source (the backend worker in the browser, a fixture in a
 * test), and the request. That is what makes the whole driver a Node test
 * with the REAL engine and fake history, and it is why every decision below
 * is visible here rather than spread across the worker and the UI.
 *
 * The order of operations is the plan's chunk 4, and each step can refuse:
 *   1. prepare   — a syntax error is the whole answer
 *   2. inputs    — every outside path the wiring connects an input to; one that
 *                  cannot be replayed is set aside with its reason, not the
 *                  whole run
 *   3. bounds    — a controller with no history, or none in this window, takes
 *                  its own inputs out; the run refuses only when nothing is left
 *   4. seed      — the last value of each input before t0 (decision 6)
 *   5. body      — every recorded change in [t0, t1), merged by time; the row
 *                  cap is enforced BEFORE anything boots (decision 9)
 *   6. run       — boot at t0 from the seeds, then each row at its own
 *                  instant, then advance to t1 so trailing timers fire. The
 *                  seeds are the current values the model starts from, with
 *                  no event, as model-runner starts a model from what the
 *                  broker holds.
 *   7. assemble  — the trace, what was entered and never entered, a summary
 *
 * History wins (decision 7) without any code here: each recorded row carries
 * `path` and `value`, and the engine puts the value into each input that reads
 * the path before the macrostep, as it does on the edge.
 *
 * WHAT RUNS (Part 7 of docs/hierarchical-models-step-3-plan.md). A model with
 * children, or with wiring of its own, runs as a group: a group of one when it
 * has no children, as on the edge. Only a group reads outside paths, because
 * only the wiring connects an input to a path. A model with no wiring is a
 * single model: it reads nothing from outside, so it has nothing to replay.
 */
import type { GroupSpec, ScxmlTransition } from '@vitesse/statechart-host';
import type { TypedHistory, TypedSample } from '../../core/bridge/history-typed';
import type { SimulateOutcome, SimulateRun } from './closed-loop';
import type { Prepared, RunOutcome, RunStep, TraceEntry } from './protocol';
import { entryCounts, stateChanges, type Transition } from './scan-trace';

/** What the driver needs from a history store. The browser adapts the backend
 *  worker to this (history-source.ts); a test hands in fixtures. */
export interface HistorySource {
  /** The recorded span for a controller scope, or null if that could not be
   *  determined. `model:<id>` is a controller scope too. */
  bounds(controllerId: string): Promise<{ hasData: boolean; minTsMs: number; maxTsMs: number } | null>;
  /** Every recorded change of the given symbols in [t1Ms, t2Ms), typed and
   *  ordered — chunk 1's reader. Symbols are display paths for a controller
   *  scope and verbatim variable ids for a model scope. */
  range(controllerId: string, symbols: string[], t1Ms: number, t2Ms: number): Promise<TypedHistory>;
  /** How many rows each symbol has in each epoch-aligned bucket of `bucketMs`
   *  within [t1Ms, t2Ms) — only buckets with rows. Optional: a source that
   *  cannot count leaves it out, and the search walks fixed slices instead. */
  counts?(controllerId: string, symbols: string[], t1Ms: number, t2Ms: number, bucketMs: number): Promise<HistoryCount[]>;
}

/** One symbol's row count in one bucket. */
export interface HistoryCount {
  symbol: string;
  /** Bucket start, ms since epoch, aligned to the bucket size. */
  tsMs: number;
  count: number;
}

/** What the driver needs from an engine. The sandbox session satisfies it. */
export interface ReplayEngine {
  /** The prepare answer for a single model. */
  prepare(scxml: string): Promise<Prepared>;
  /** Start a single model at `startNs`. It has no wiring, so it reads no value from outside. */
  boot(scxml: string, outputs: string[], startNs: bigint): Promise<{ state: string[]; trace: TraceEntry[] }>;
  /** The prepare answer for a group (Part 5 of docs/hierarchical-models-step-3-plan.md). */
  prepareGroup(spec: GroupSpec): Promise<Prepared>;
  /** Start a group at `startNs` from the current values `values` (`[path, value]`), with no event for them. */
  bootGroup(spec: GroupSpec, startNs: bigint, values?: Array<[string, unknown]>): Promise<{ state: string[]; trace: TraceEntry[] }>;
  /** `mode: 'scan'` asks for activity only — see scan-trace.ts. An engine
   *  that streams everything regardless is still correct; the search filters
   *  again on arrival. */
  run(steps: RunStep[], opts?: { onTrace?: (entries: TraceEntry[]) => void; mode?: 'full' | 'scan' }): Promise<RunOutcome>;
  /** Run the closed loop (closed-loop.ts) beside the engine, after `boot`. An
   *  engine without it cannot simulate, and `simulate()` says so. */
  simulate?(run: SimulateRun, opts?: { onTrace?: (entries: TraceEntry[]) => void }): Promise<SimulateOutcome>;
  dispose(): void;
}

export interface ReplayRequest {
  scxml: string;
  /**
   * A model that runs as a group: its children, or a root with its own wiring, which runs as a
   * group of one (Part 5 of docs/hierarchical-models-step-3-plan.md). The replay then boots the
   * group, drives every outside path its wiring reads, and names each child's states
   * `<child>/<state>`. `scxml` is then the root's, for the record. Without it, the model is a
   * single model, which reads nothing from outside, so the replay refuses.
   */
  group?: GroupSpec;
  /** Declared output names of a single model; only these produce `variable` trace entries. A group's are in its spec. */
  outputs?: string[];
  /** The window, ms since epoch. */
  t0Ms: number;
  t1Ms: number;
  /** How far before t0 to look for each input's starting value. Default 1 h. */
  seedLookbackMs?: number;
  /** Refuse a window with more recorded changes than this. Default 1,000,000 —
   *  the Python sandbox's figure. */
  maxRows?: number;
  /** Rows per engine run call, so a large replay streams rather than crossing
   *  in one message. Default 5,000. */
  stepsPerRun?: number;
  onProgress?: (rowsDone: number, rowsTotal: number) => void;
}

/** One thing the statechart reads, resolved to where its history lives. */
export interface ReplayInput {
  /** The entity path the statechart reads: controllers/…/symbols/… or models/…/variables/…. */
  path: string;
  /** The input that reads this path: the root's input by its name, a child's as `<child>.<port>`. Null when no input name is known. */
  name: string | null;
  kind: 'controller' | 'model';
  /** The history scope: the controller id, or `model:<id>`. */
  controllerId: string;
  /**
   * The symbol exactly as the path spells it — for a controller that is the
   * ENCODED subject token (`DB10_Motor1_Speed`), because that is what a wired
   * path holds and what the edge puts in `symbol_id`. Use it for the event, never
   * for the historian.
   */
  symbol: string;
  /**
   * The same symbol DECODED (`DB10.Motor1.Speed`) — what the historian is asked
   * with (the typed reader encodes it again on the way out) and what a person
   * reads. For a model variable the two are identical: variable names are not
   * subject-encoded.
   *
   * THE DISTINCTION IS LOAD-BEARING. Handing the reader the encoded token makes
   * it encode a second time (`_` → `__`), which asks for a symbol that does not
   * exist and returns no rows — an empty replay that looks exactly like a model
   * that did nothing.
   */
  symbolDecoded: string;
  /** Recorded changes inside the window. */
  rows: number;
  /** The value in force at t0, from the look-back — or null if none was found. */
  seed: TypedSample | null;
  /** When an unseeded input first got a value inside the window, or null if it
   *  never did. Until then it had no value: a guard that read it was false. */
  unknownUntilNs: bigint | null;
}

/** A recorded sample, with the entity path it belongs to. */
export type ReplaySample = TypedSample & { path: string };

/**
 * AN INPUT THE REPLAY SET ASIDE, RATHER THAN DYING ON.
 *
 * One connected input the driver cannot handle must not become the whole
 * answer while the four beside it would have drawn fine. It is set aside with its reason and
 * the rest go on — and the panel says which were set aside, because a chart
 * that quietly draws four of five lanes reads as a complete picture when it is
 * not.
 *
 * Kept APART from `inputs`. A skipped path has no samples, no seed and no lane;
 * everything that maps over `inputs` assumes all three, and an empty lane for a
 * path that was never fetched would claim "recorded nothing", which is a
 * different and false statement from "could not be replayed".
 */
export type SkippedInput = {
  /** The input that reads the path, as `ReplayInput.name` names it. */
  name: string | null;
  path: string;
  /** Why, as the English sentence the refusal would have been — for the AI's
   *  summary. A person's view says it in their language, from the fields below. */
  reason: string;
} &
  /** An integration datapoint, which the historian never records — or not an entity path at all. */
  (
    | { why: 'integration-path' | 'not-a-path' }
    /** The scope has never recorded anything. */
    | { why: 'no-history'; scope: string }
    /** The window lies wholly after the scope's history ends, or before it begins; `atMs` is that edge. */
    | { why: 'window-after' | 'window-before'; scope: string; atMs: number }
  );

/**
 * WHAT THE MODEL WOULD HAVE WRITTEN, BESIDE WHAT THAT PATH ACTUALLY DID.
 *
 * The one thing a replay is reliably misread as: "the model opened the drain,
 * so why does the level keep climbing?" It climbs because nothing received the
 * decision — the recording is the plant as it actually ran, and it cannot
 * respond to a valve that was never opened.
 *
 * A caveat in prose does not fix that; the two facts have to sit on the same
 * strip. For every path the model would have written, this carries the model's
 * intent and that path's OWN recorded history over the same window, so
 * "would have opened it at 22 past" and "it stayed shut all hour" are one
 * picture rather than two claims.
 */
export interface ReplayContrast {
  /** The entity path the model would have written. */
  path: string;
  /** Its symbol, decoded — what a person reads. */
  symbolDecoded: string;
  /** Every write the replay captured for this path, in order. */
  wrote: { tsNs: bigint; value: unknown }[];
  /**
   * What that path actually recorded in the window: an empty array means the
   * historian holds nothing for it, `null` means it was never asked (an
   * integration path, which is never historized, or a value that is not an
   * entity path at all). The two are different answers and must not merge.
   */
  recorded: TypedSample[] | null;
  /** What it held when the window opened: its last row in the look-back, or null when there is none
   *  or it was never asked. Without it a write at t0 could not be told from the value already there. */
  seed: TypedSample | null;
  /** Why `recorded` is null, for the reader. Empty when it was fetched. */
  note: string;
}

export interface ReplayResult {
  ok: true;
  /** The window as replayed. */
  window: { t0Ns: bigint; t1Ns: bigint };
  inputs: ReplayInput[];
  /** Connected inputs that were set aside, with why. Never folded into `inputs`. */
  skipped: SkippedInput[];
  /** Every recorded change that was fed in, merged by time — the viewer's lanes. */
  samples: ReplaySample[];
  trace: TraceEntry[];
  initialState: string[];
  finalState: string[];
  /** Every state the document declares, in document order. */
  stateIds: string[];
  /** Every transition the document declares, with its condition. */
  transitions: ScxmlTransition[];
  /** One entry per distinct path the model would have written. */
  contrast: ReplayContrast[];
  /**
   * The instant the replay stops being an exact account and becomes a
   * hypothesis: the model's FIRST write of a value its path did not already
   * hold (`firstChangingWrite`). Up to here it was a pure observer of the past,
   * so what it decided is what it would have decided. After here a real plant
   * would have reacted, and the recording — which did not — is the only input
   * there is. Null when the model never tried to change anything, in which
   * case the whole replay is exact.
   */
  divergedAtNs: bigint | null;
  /** state id → how many times the replay entered it. */
  visited: Record<string, number>;
  neverEntered: string[];
  hung: boolean;
  /** Non-routine things the backend said, retention narrowing above all. */
  notes: string[];
  /** Inputs whose history was cut short by the reader's page cap. */
  truncated: string[];
  rowCount: number;
  /** The paragraph the AI tool returns and a person reads first. */
  summary: string;
  /** The same paragraph one fact at a time, for the viewer's Details: `summary` is their texts joined. */
  summaryLines?: SummaryLine[];
}

/**
 * One fact of a summary, as the viewer lists it. `text` is the sentence as the AI reads it.
 * `kind` marks a line that comes in a run of its kind (one sentence for each symbol), so the
 * viewer can show the first few and count the rest. `list` is a long list inside the sentence:
 * the text is `before`, the items joined by `sep`, and `after`.
 */
export interface SummaryLine {
  text: string;
  kind?: string;
  list?: { before: string; items: string[]; sep: string; after: string };
}

/** Collects a summary's lines: `say` for a sentence, `sayList` for a sentence around a long list. */
export function summaryWriter(): {
  lines: SummaryLine[];
  say: (text: string, kind?: string) => void;
  sayList: (before: string, items: readonly string[], sep: string, after: string) => void;
} {
  const lines: SummaryLine[] = [];
  return {
    lines,
    say: (text, kind) => void lines.push(kind ? { text, kind } : { text }),
    sayList: (before, items, sep, after) =>
      void lines.push({ text: `${before}${items.join(sep)}${after}`, list: { before, items: [...items], sep, after } }),
  };
}

/** The paragraph the AI reads, from its lines. */
export const joinSummary = (lines: readonly SummaryLine[]): string => lines.map((l) => l.text).join(' ');

export interface ReplayRefusal {
  ok: false;
  refusal: string;
}

const MS_NS = 1_000_000n;
const DEFAULT_LOOKBACK_MS = 3_600_000;
const DEFAULT_MAX_ROWS = 1_000_000;
const DEFAULT_STEPS_PER_RUN = 5_000;

/** A count grouped the way the sentence around it is written — English, whatever the UI language.
 *  Not `fmtNum`: that follows the app's language, and these counts sit inside untranslated English
 *  prose that the assistant reads, where "1.000.000" would read as a decimal. Not the host's locale
 *  either, which is neither. */
const count = (n: number): string => n.toLocaleString('en-US');

/** The sentence models.write already uses for a document that reads nothing. */
export const NO_LIVE_INPUTS = 'Refused: as written this model can never receive an event.';

/** The note for a scope whose history bounds could not be fetched. Exported so
 *  the person-facing view can drop it once readings came back anyway — they
 *  settle the question it leaves open. */
export function boundsUnknownNote(scope: string): string {
  return `Could not check whether ${scope} has any recorded history; an empty replay for it is not evidence that it was never recorded.`;
}

const CONTROLLER_PATH = /^controllers\/([^/]+)\/symbols\/(.+)$/;
const MODEL_PATH = /^models\/([^/]+)\/variables\/(.+)$/;
const INTEGRATION_PATH = /^integrations\//;

/** Resolve an entity path to where its history lives, or say why it cannot be replayed. */
export function classifyPath(path: string, name: string | null): ReplayInput | { refusal: string } {
  let m = CONTROLLER_PATH.exec(path);
  if (m) {
    return {
      path,
      name,
      kind: 'controller',
      controllerId: m[1],
      symbol: m[2],
      // A binding names the symbol by its display path (ModelDescriptor.symbol_paths).
      symbolDecoded: m[2],
      rows: 0,
      seed: null,
      unknownUntilNs: null,
    };
  }
  m = MODEL_PATH.exec(path);
  if (m) {
    return {
      path,
      name,
      kind: 'model',
      controllerId: `model:${m[1]}`,
      symbol: m[2],
      symbolDecoded: m[2],
      rows: 0,
      seed: null,
      unknownUntilNs: null,
    };
  }
  if (INTEGRATION_PATH.test(path)) {
    return {
      refusal:
        `Refused: ${name ? `"${name}" is connected to ` : 'the model reads '}${path}, and integration datapoints are never recorded by the historian — the external system is the system of record, by design. ` +
        'A replay can use controller symbols and other models’ outputs; to replay integration data, it would have to come from the integration’s own read RPC, which this sandbox does not do.',
    };
  }
  return {
    refusal: `Refused: ${name ? `"${name}" is connected to "${path}", which` : `the model reads "${path}", which`} is not an entity path. A connection is a full path: controllers/<controller>/symbols/<symbol> or models/<model>/variables/<name>.`,
  };
}

/** The event the model-runner's host builds for a value on this path — the
 *  same shape, so the engine cannot tell replay from live. `path` and `value`
 *  are what put the value into each input that reads the path before the
 *  macrostep. */
export function eventFor(input: ReplayInput, value: unknown): { event: string; data: Record<string, unknown> } {
  if (input.kind === 'controller') {
    return {
      event: 'symbolValueChanged',
      data: { controller_id: input.controllerId, symbol_id: input.symbol, value, path: input.path },
    };
  }
  return { event: 'valueChanged', data: { path: input.path, value } };
}

/** The active configuration at a virtual instant: the last transition at or
 *  before it. Before the first transition, nothing is active. */
export function activeAt(trace: TraceEntry[], tsNs: bigint): string[] {
  let lo = 0;
  let hi = trace.length - 1;
  let best: string[] = [];
  // Transitions are a subsequence of the trace in time order; scan is fine for
  // the viewer's cursor, but a binary search over the whole trace keeps a
  // million-entry trace responsive under a drag.
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (trace[mid].tsNs <= tsNs) {
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  for (let i = lo - 1; i >= 0; i--) {
    const e = trace[i];
    if (e.kind === 'transition') {
      best = e.to;
      break;
    }
  }
  return best;
}

/** A refusal sentence as a skipped input's reason: the same words, without the
 *  "Refused: " that makes it the whole answer. */
const asReason = (refusal: string): string => refusal.replace(/^Refused: /, '');

/** The one refusal left once every input has been set aside: all the reasons,
 *  so a person fixes them in one pass rather than one run at a time. */
export const refuseAll = (skipped: readonly SkippedInput[]): ReplayRefusal => ({
  ok: false,
  refusal: `Refused: ${skipped.map((s) => s.reason).join(' ')}`,
});

/** Add what the backend said, once each. */
export function mergeNotes(notes: string[], more: readonly string[]): void {
  for (const n of more) if (!notes.includes(n)) notes.push(n);
}

/** Time order for anything stamped in bigint nanoseconds. Stable under
 *  Array.prototype.sort, so each source's own tie-break survives. */
export const byTime = (a: { tsNs: bigint }, b: { tsNs: bigint }): number => (a.tsNs < b.tsNs ? -1 : a.tsNs > b.tsNs ? 1 : 0);

/** The inputs that share one history scope, and how a returned row finds its
 *  input. Rows come back keyed by the DECODED symbol they were asked with. */
export interface ScopeGroup {
  inputs: ReplayInput[];
  /** The decoded symbols, in input order — what the historian is asked for. */
  symbols: string[];
  bySymbol: Map<string, ReplayInput>;
}

export function groupByScope(inputs: readonly ReplayInput[]): Map<string, ScopeGroup> {
  const out = new Map<string, ScopeGroup>();
  for (const i of inputs) {
    let g = out.get(i.controllerId);
    if (!g) {
      g = { inputs: [], symbols: [], bySymbol: new Map() };
      out.set(i.controllerId, g);
    }
    g.inputs.push(i);
    g.symbols.push(i.symbolDecoded);
    g.bySymbol.set(i.symbolDecoded, i);
  }
  return out;
}

/**
 * WHICH SCOPES HAVE A PAST IN THIS WINDOW — shared by the replay and the search,
 * so the two set aside the same inputs for the same reasons.
 *
 * PER SCOPE, NOT PER RUN. A controller with no history, or none in this
 * window, takes its own inputs out and only its own; each carries its reason
 * into `skipped`. A scope whose bounds could not be fetched is kept, with a
 * note. The caller refuses only when no input is left.
 *
 * `earliestMs` is the earliest recording among the scopes kept, or null when
 * any kept scope's bounds are unknown — the search starts there rather than
 * days before anything was recorded, and only when it can be sure.
 */
export async function checkHistoryBounds(
  inputs: readonly ReplayInput[],
  skipped: SkippedInput[],
  history: Pick<HistorySource, 'bounds'>,
  t0Ms: number,
  t1Ms: number,
): Promise<{ inputs: ReplayInput[]; notes: string[]; earliestMs: number | null }> {
  const notes: string[] = [];
  let kept = [...inputs];
  let earliest = Infinity;
  let unknown = false;
  type ScopeDetail = { why: 'no-history'; scope: string } | { why: 'window-after' | 'window-before'; scope: string; atMs: number };
  const dropScope = (detail: ScopeDetail, reason: string) => {
    for (const i of kept) if (i.controllerId === detail.scope) skipped.push({ name: i.name, path: i.path, reason, ...detail });
    kept = kept.filter((i) => i.controllerId !== detail.scope);
  };
  for (const scope of new Set(inputs.map((i) => i.controllerId))) {
    const b = await history.bounds(scope);
    if (b === null) {
      notes.push(boundsUnknownNote(scope));
      unknown = true;
      continue;
    }
    if (!b.hasData) {
      const symbols = kept.filter((i) => i.controllerId === scope).map((i) => i.symbolDecoded);
      dropScope(
        { why: 'no-history', scope },
        `${scope} has no recorded history at all — not for ${symbols.join(', ')}, and not for anything else on it. The historian has never stored samples for this scope.`,
      );
    } else if (t0Ms > b.maxTsMs) {
      dropScope(
        { why: 'window-after', scope, atMs: b.maxTsMs },
        `the window starts after ${scope}’s recorded history ends (${new Date(b.maxTsMs).toISOString()}). There is a past for it, and you asked after it ended.`,
      );
    } else if (t1Ms < b.minTsMs) {
      dropScope(
        { why: 'window-before', scope, atMs: b.minTsMs },
        `the window ends before ${scope}’s recorded history begins (${new Date(b.minTsMs).toISOString()}). There is a past for it, and you asked before it started.`,
      );
    } else {
      earliest = Math.min(earliest, b.minTsMs);
    }
  }
  return { inputs: kept, notes, earliestMs: unknown || earliest === Infinity ? null : earliest };
}

/**
 * The value in force at t0 for each input: the last one recorded in the
 * look-back before it. Sets `seed` on each input; returns nothing else because
 * the seeds travel with the inputs.
 */
export async function fetchSeeds(
  groups: ReadonlyMap<string, ScopeGroup>,
  history: Pick<HistorySource, 'range'>,
  t0Ms: number,
  lookbackMs: number,
  notes: string[],
): Promise<void> {
  for (const [scope, g] of groups) {
    const h = await history.range(scope, g.symbols, t0Ms - lookbackMs, t0Ms);
    mergeNotes(notes, h.notes);
    for (const s of h.samples) {
      const input = g.bySymbol.get(s.symbol);
      if (input) input.seed = s; // ascending order → the last one wins
    }
  }
}

/**
 * Every recorded change of each input inside the window, appended to `samples`
 * with its path and counted on its input; a symbol the reader cut short lands
 * in `truncated`. Across scopes, one chronological stream: each scope's stream
 * is already ordered, so a stable sort by time keeps the reader's tie-break
 * (symbol order within a scope) and adds scope order after it — deterministic.
 */
export async function fetchBody(
  groups: ReadonlyMap<string, ScopeGroup>,
  history: Pick<HistorySource, 'range'>,
  t0Ms: number,
  t1Ms: number,
  notes: string[],
  samples: ReplaySample[],
  truncated: string[],
): Promise<void> {
  for (const [scope, g] of groups) {
    const body = await history.range(scope, g.symbols, t0Ms, t1Ms);
    mergeNotes(notes, body.notes);
    for (const sym of body.truncated) {
      const input = g.bySymbol.get(sym);
      if (input) truncated.push(input.path);
    }
    for (const s of body.samples) {
      const input = g.bySymbol.get(s.symbol);
      if (!input) continue;
      input.rows++;
      samples.push({ ...s, path: input.path });
    }
  }
  samples.sort(byTime);
}

/** The seeds as the current values a model boots from, `[path, value]`, in
 *  input order. model-runner reads each input's current value from the broker
 *  before it starts a model, and gives it no event (WorkerHostPool.dispatchBoot),
 *  so neither does the sandbox. */
export function seedValues(inputs: readonly ReplayInput[]): Array<[string, unknown]> {
  const values: Array<[string, unknown]> = [];
  for (const input of inputs) if (input.seed !== null) values.push([input.path, input.seed.value]);
  return values;
}

/**
 * The counts a replay is described by — shared by the summary a person and the
 * AI read and the tool's structured outcome, so the two never disagree.
 * `moves` are real state changes after boot (see `stateChanges`), never the
 * engine's one-per-event transitions.
 */
export function replayCounts(trace: readonly TraceEntry[]): {
  moves: Transition[];
  movesByEvent: Record<string, number>;
  writes: Extract<TraceEntry, { kind: 'write' }>[];
  errors: Extract<TraceEntry, { kind: 'error' }>[];
} {
  const moves = stateChanges(trace).slice(1);
  const movesByEvent: Record<string, number> = {};
  for (const m of moves) movesByEvent[m.event] = (movesByEvent[m.event] ?? 0) + 1;
  return {
    moves,
    movesByEvent,
    writes: trace.filter((e): e is Extract<TraceEntry, { kind: 'write' }> => e.kind === 'write'),
    errors: trace.filter((e): e is Extract<TraceEntry, { kind: 'error' }> => e.kind === 'error'),
  };
}

/**
 * Everything the model reads — each connected input by its name, then each
 * outside path its wiring reads (`Prepared.readPaths`) — resolved to where
 * each one's history lives. Shared by the replay, the search and the
 * simulation.
 *
 * ONE BAD CONNECTION IS SET ASIDE, NOT THE WHOLE RUN. A path that cannot be
 * replayed lands in `skipped` with its reason and the others go on; the call
 * refuses only when nothing at all is left, and then names every reason.
 */
export function resolveInputs(
  named: Record<string, string>,
  readPaths: readonly string[],
): { ok: true; inputs: ReplayInput[]; skipped: SkippedInput[] } | ReplayRefusal {
  const inputs: ReplayInput[] = [];
  const skipped: SkippedInput[] = [];
  const seen = new Set<string>();
  const consider = (path: string, name: string | null): void => {
    if (seen.has(path)) return;
    const c = classifyPath(path, name);
    if ('refusal' in c) {
      skipped.push({ name, path, reason: asReason(c.refusal), why: INTEGRATION_PATH.test(path) ? 'integration-path' : 'not-a-path' });
      return;
    }
    seen.add(path);
    inputs.push(c);
  };
  for (const [name, path] of Object.entries(named)) consider(path, name);
  for (const path of readPaths) consider(path, null);
  if (inputs.length === 0) {
    if (skipped.length) return refuseAll(skipped);
    return {
      ok: false,
      refusal: `${NO_LIVE_INPUTS} No input of it, or of a child, is connected to a controller symbol or another model's output, so there is nothing to replay.`,
    };
  }
  return { ok: true, inputs, skipped };
}

/**
 * What each written path actually did — fetched AFTER the run, because the
 * write paths are what the model TOOK, not what it declares: a branch never
 * reached writes nothing, and inventing a lane for it would be a claim the
 * replay cannot make. Each path's seed comes from the `lookbackMs` before t0,
 * as an input's does.
 */
export async function fetchContrast(
  trace: readonly TraceEntry[],
  history: Pick<HistorySource, 'range'>,
  t0Ms: number,
  t1Ms: number,
  lookbackMs: number,
  notes: string[],
): Promise<ReplayContrast[]> {
  const contrast: ReplayContrast[] = [];
  const wroteBy = new Map<string, { tsNs: bigint; value: unknown }[]>();
  for (const e of trace) {
    if (e.kind !== 'write') continue;
    const list = wroteBy.get(e.path);
    if (list) list.push({ tsNs: e.tsNs, value: e.value });
    else wroteBy.set(e.path, [{ tsNs: e.tsNs, value: e.value }]);
  }
  const contrastByScope = new Map<string, { entry: ReplayContrast; symbol: string }[]>();
  for (const [path, wrote] of wroteBy) {
    const c = classifyPath(path, null);
    if ('refusal' in c) {
      contrast.push({
        path,
        symbolDecoded: path,
        wrote,
        recorded: null,
        seed: null,
        note: INTEGRATION_PATH.test(path)
          ? 'Integration datapoints are never recorded by the historian, so there is nothing to compare this against.'
          : 'This is not an entity path, so the historian holds no history for it.',
      });
      continue;
    }
    const entry: ReplayContrast = { path, symbolDecoded: c.symbolDecoded, wrote, recorded: [], seed: null, note: '' };
    contrast.push(entry);
    const group = contrastByScope.get(c.controllerId);
    if (group) group.push({ entry, symbol: c.symbolDecoded });
    else contrastByScope.set(c.controllerId, [{ entry, symbol: c.symbolDecoded }]);
  }
  for (const [scope, group] of contrastByScope) {
    const symbols = group.map((g) => g.symbol);
    try {
      const before = await history.range(scope, symbols, t0Ms - lookbackMs, t0Ms);
      const h = await history.range(scope, symbols, t0Ms, t1Ms);
      mergeNotes(notes, before.notes);
      mergeNotes(notes, h.notes);
      for (const g of group) {
        g.entry.seed = before.samples.findLast((s) => s.symbol === g.symbol) ?? null;
        g.entry.recorded = h.samples.filter((s) => s.symbol === g.symbol);
      }
    } catch (e) {
      // A failed fetch leaves the replay itself untouched, and the note carries what went wrong.
      // With nothing to compare against, every write counts as a change (`firstChangingWrite`).
      const why = e instanceof Error ? e.message : String(e);
      for (const g of group) {
        g.entry.recorded = null;
        g.entry.note = `Its recorded history could not be fetched (${why}), so no comparison is shown.`;
      }
    }
  }
  return contrast;
}

/**
 * When the model first wrote a value its path did not already hold: the divergence point. A model
 * that sets its outputs to off as it starts, while they are off, has changed nothing the plant got,
 * so the recording after that write is still the plant this model would have had. The held value is
 * the path's last recorded row at or before the write, or its seed. A path with no recording, or none
 * yet, holds an unknown value, and a write to it always counts.
 */
export function firstChangingWrite(trace: readonly TraceEntry[], contrast: readonly ReplayContrast[]): bigint | null {
  const byPath = new Map(contrast.map((c) => [c.path, c]));
  for (const e of trace) {
    if (e.kind !== 'write') continue;
    const c = byPath.get(e.path);
    let held = c?.seed?.value ?? null;
    for (const s of c?.recorded ?? []) {
      if (s.tsNs > e.tsNs) break;
      held = s.value;
    }
    if (held === null || held !== e.value) return e.tsNs;
  }
  return null;
}

/** `{i}` or `{n}` in a path of an array child: the index of a machine, from 0 or from 1 (Step 4). */
const INDEXED = /\{[in]\}/;

/**
 * Each path a group reads from outside, by the port that reads it: the root's own name, `<child>.<port>` for a child's.
 * Every path of the root's wiring is in it, so one that is no entity path is set aside with its reason. A child's
 * input can also read another machine, which is inside the group, so only its outside paths are in it.
 *
 * A path of an array child that holds `{i}` or `{n}` is one path for each machine, named as the host names that
 * machine's port: `sensors[3].raw` reads `In4` for `In{n}`. These are the paths of statechart-host's `readPathsOf`,
 * by the rule of its `pathsFor`, which this module does not import: the shell loads the host only on first use.
 * test/model-write-array.test.ts holds the two together. A path with no index is one path for the whole array.
 */
export function inputNames(group: GroupSpec | undefined): Record<string, string> {
  const names: Record<string, string> = {};
  if (!group) return names;
  const outside = (path: string): boolean => /^(controllers|models|integrations)\//.test(path);
  for (const [port, b] of Object.entries(group.root.wiring?.inputs ?? {})) if ('path' in b && b.path !== '') names[port] = b.path;
  for (const child of group.children)
    for (const [port, b] of Object.entries(child.inputs ?? {})) {
      if (!('path' in b) || !outside(b.path)) continue;
      // The host's `countOf`: a count that the group refuses gives no machine here.
      const count = child.count !== undefined && Number.isInteger(child.count) && child.count >= 1 && child.count <= 256 ? child.count : 0;
      if (count === 0 || !INDEXED.test(b.path)) names[`${child.name}.${port}`] = b.path;
      else
        for (let i = 0; i < count; i++)
          names[`${child.name}[${i}].${port}`] = b.path.replace(/\{i\}/g, String(i)).replace(/\{n\}/g, String(i + 1));
    }
  return names;
}

/** The prepare answer for what the request runs: its group, or its single model. Shared by the replay, the search and the simulation. */
export const prepareFor = (req: Pick<ReplayRequest, 'scxml' | 'group'>, engine: ReplayEngine): Promise<Prepared> =>
  req.group ? engine.prepareGroup(req.group) : engine.prepare(req.scxml);

/** The start of what the request runs at `startNs`: its group from the current values, or its single model, which reads none. */
export const bootFor = (
  req: Pick<ReplayRequest, 'scxml' | 'group' | 'outputs'>,
  engine: ReplayEngine,
  startNs: bigint,
  values: Array<[string, unknown]>,
): Promise<{ state: string[]; trace: TraceEntry[] }> =>
  req.group ? engine.bootGroup(req.group, startNs, values) : engine.boot(req.scxml, req.outputs ?? [], startNs);

export async function replay(req: ReplayRequest, engine: ReplayEngine, history: HistorySource): Promise<ReplayResult | ReplayRefusal> {
  const t0Ns = BigInt(Math.floor(req.t0Ms)) * MS_NS;
  const t1Ns = BigInt(Math.floor(req.t1Ms)) * MS_NS;
  if (!(req.t1Ms > req.t0Ms)) return { ok: false, refusal: 'Refused: the window ends before it starts.' };

  // ── 1. prepare ────────────────────────────────────────────────────────────
  const prepared = await prepareFor(req, engine);
  if (prepared.error) return { ok: false, refusal: `Refused: the statechart has a JavaScript syntax error — ${prepared.error}` };

  // ── 2. inputs ─────────────────────────────────────────────────────────────
  // Named by the port that reads each path, as `<child>.<port>` for a child's.
  const resolved = resolveInputs(inputNames(req.group), prepared.readPaths);
  if (!resolved.ok) return resolved;
  const skipped = resolved.skipped;

  // ── 3. bounds ─────────────────────────────────────────────────────────────
  const checked = await checkHistoryBounds(resolved.inputs, skipped, history, req.t0Ms, req.t1Ms);
  const inputs = checked.inputs;
  const notes = checked.notes;
  if (inputs.length === 0) return refuseAll(skipped);

  // ── 4. seed + 5. body ─────────────────────────────────────────────────────
  const maxRows = req.maxRows ?? DEFAULT_MAX_ROWS;
  const truncated: string[] = [];
  const samples: ReplaySample[] = [];
  // DECODED symbols: the reader encodes what it is given, and a wired path
  // already holds the encoded token. Rows come back keyed by what we asked
  // with, so every match below is against the decoded form too.
  const groups = groupByScope(inputs);
  await fetchSeeds(groups, history, req.t0Ms, req.seedLookbackMs ?? DEFAULT_LOOKBACK_MS, notes);
  await fetchBody(groups, history, req.t0Ms, req.t1Ms, notes, samples, truncated);

  if (samples.length > maxRows) {
    const windowMs = req.t1Ms - req.t0Ms;
    const fitMin = Math.max(1, Math.floor((windowMs * (maxRows / samples.length)) / 60_000));
    return {
      ok: false,
      refusal: `Refused: that window holds ${count(samples.length)} recorded changes across ${inputs.length} input(s), over the ${count(maxRows)} the sandbox will replay. Narrow it — about ${fitMin} minute(s) of this data would fit — or replay fewer inputs.`,
    };
  }
  for (const input of inputs) {
    if (input.seed !== null) continue;
    const first = samples.find((s) => s.path === input.path);
    input.unknownUntilNs = first ? first.tsNs : null;
  }

  // ── 6. run ────────────────────────────────────────────────────────────────
  const trace: TraceEntry[] = [];
  const booted = await bootFor(req, engine, t0Ns, seedValues(inputs));
  trace.push(...booted.trace);
  const initialState = booted.state;

  // Every recorded row at its own instant; the seeds are in the boot.
  const steps: RunStep[] = [];
  const inputByPath = new Map(inputs.map((i) => [i.path, i]));
  for (const s of samples) {
    const { event, data } = eventFor(inputByPath.get(s.path)!, s.value);
    steps.push({ event, data, tsNs: s.tsNs });
  }
  // Trailing timers: a `<send delay>` armed by the last row still has to fire
  // if it falls inside the window.
  steps.push({ advanceTo: t1Ns });

  const perRun = req.stepsPerRun ?? DEFAULT_STEPS_PER_RUN;
  let finalState = initialState;
  let hung = false;
  let done = 0;
  for (let i = 0; i < steps.length && !hung; i += perRun) {
    const chunk = steps.slice(i, i + perRun);
    const out = await engine.run(chunk, { onTrace: (es) => trace.push(...es) });
    // `run` hands the whole chunk's trace back on completion as well; entries
    // already pushed by onTrace are the same objects, so take its count as
    // authoritative rather than appending twice.
    trace.length = trace.length - out.trace.length + out.trace.length;
    finalState = out.state;
    hung = out.hung;
    done = Math.min(steps.length, i + chunk.length);
    req.onProgress?.(done, steps.length);
  }

  // ── 6b. contrast: what each written path actually did ─────────────────────
  const contrast = await fetchContrast(trace, history, req.t0Ms, req.t1Ms, req.seedLookbackMs ?? DEFAULT_LOOKBACK_MS, notes);

  // ── 7. assemble ───────────────────────────────────────────────────────────
  // Boot included: the starting state counts as entered, so it is never "never entered".
  const visited = Object.fromEntries(entryCounts(stateChanges(trace)));
  const neverEntered = prepared.stateIds.filter((s) => !(s in visited));

  const result: ReplayResult = {
    ok: true,
    window: { t0Ns, t1Ns },
    inputs,
    skipped,
    samples,
    trace,
    initialState,
    finalState,
    stateIds: prepared.stateIds,
    transitions: prepared.transitions,
    contrast,
    divergedAtNs: firstChangingWrite(trace, contrast),
    visited,
    neverEntered,
    hung,
    notes,
    truncated,
    rowCount: samples.length,
    summary: '',
  };
  result.summaryLines = summaryLines(result);
  result.summary = joinSummary(result.summaryLines);
  return result;
}

/** A state of a machine of an array, `sensors/3/blocked`: the array's name, the index and the state. A state id has no `/`. */
const ARRAY_STATE = /^([^/]+)\/(\d+)\/(.+)$/;
/** How many machines the summary names for one state of an array; it counts the rest. */
const MACHINES_NAMED = 10;

/** One state as the summary names it: a state of the root or of a single child, or one state of every machine of an array. */
interface FoldedState {
  // `blocked`, `ps1/blocked`, or `sensors/*/blocked` for every machine of the array `sensors`.
  state: string;
  /** The sum of the counts that were folded. */
  n: number;
  /** For an array: its name, and the machines that were folded, as `sensors/3`. */
  array: { name: string; machines: string[] } | null;
}

// States with a count each, as the summary lists them (Step 4): each state of the machines of an
// array is one entry, `sensors/*/blocked`, with the counts added and the machines listed. The other
// states stay as they are, in their order. An array can have 256 machines, and a list of each
// machine's states would fill the summary. The trace and the result keep each machine's states.
function foldStates(entries: Iterable<readonly [string, number]>): FoldedState[] {
  const out: FoldedState[] = [];
  const byKey = new Map<string, FoldedState>();
  for (const [state, n] of entries) {
    const m = ARRAY_STATE.exec(state);
    if (!m) {
      out.push({ state, n, array: null });
      continue;
    }
    const key = `${m[1]}/*/${m[3]}`;
    const folded = byKey.get(key);
    if (folded) {
      folded.n += n;
      folded.array!.machines.push(`${m[1]}/${m[2]}`);
    } else {
      const entry: FoldedState = { state: key, n, array: { name: m[1], machines: [`${m[1]}/${m[2]}`] } };
      byKey.set(key, entry);
      out.push(entry);
    }
  }
  return out;
}

/** How many machines each array has, from the states that the prepare answer names for each machine. */
function arraySizes(stateIds: readonly string[]): Map<string, number> {
  const indices = new Map<string, Set<string>>();
  for (const id of stateIds) {
    const m = ARRAY_STATE.exec(id);
    if (!m) continue;
    const set = indices.get(m[1]);
    if (set) set.add(m[2]);
    else indices.set(m[1], new Set([m[2]]));
  }
  return new Map([...indices].map(([name, set]) => [name, set.size]));
}

/** `a, b and 3 more`: the first `listed` names, then how many more. */
const someOf = (names: readonly string[], listed: number): string =>
  names.slice(0, listed).join(', ') + (names.length > listed ? ` and ${names.length - listed} more` : '');

/** How many of an array's machines a folded state covers: `6 of 8 machines`, or `all 8 machines`. */
function machinesOf(f: FoldedState, sizes: ReadonlyMap<string, number>): string {
  const size = sizes.get(f.array!.name) ?? f.array!.machines.length;
  return f.array!.machines.length === size ? `all ${size} machines` : `${f.array!.machines.length} of ${size} machines`;
}

/** A configuration as the summaries write it, `run+ps1/blocked`, with the states of an array's machines folded. */
export function configurationText(states: readonly string[], stateIds: readonly string[]): string {
  const sizes = arraySizes(stateIds);
  return (
    foldStates(states.map((s) => [s, 1] as const))
      .map((f) => (f.array ? `${f.state} (${machinesOf(f, sizes)})` : f.state))
      .join('+') || '(nothing)'
  );
}

/** A list of states as the summaries write it, `alarm, ps1/fault`, with the states of an array's machines folded and the machines named. */
export function statesText(states: readonly string[], stateIds: readonly string[]): string {
  return stateItems(states, stateIds).join(', ');
}

/** A list of states as the data of a group with an array gives it, each in statesText's words. */
export function stateItems(states: readonly string[], stateIds: readonly string[]): string[] {
  const sizes = arraySizes(stateIds);
  return foldStates(states.map((s) => [s, 0] as const)).map((f) => foldedName(f, sizes));
}

/** Entry counts in their order, as a person's summary lists them: the states of an array's machines folded and named as stateItems names them, the counts added. */
export function enteredItems(
  entered: Iterable<readonly [string, number]>,
  stateIds: readonly string[],
): Array<{ state: string; count: number }> {
  const sizes = arraySizes(stateIds);
  return foldStates(entered).map((f) => ({ state: foldedName(f, sizes), count: f.n }));
}

/** A folded state in statesText's words, with the machines of an array that it covers. */
function foldedName(f: FoldedState, sizes: ReadonlyMap<string, number>): string {
  if (!f.array) return f.state;
  const all = f.array.machines.length === (sizes.get(f.array.name) ?? f.array.machines.length);
  return `${f.state} (${all ? `in ${machinesOf(f, sizes)}` : `in ${machinesOf(f, sizes)}: ${someOf(f.array.machines, MACHINES_NAMED)}`})`;
}

/** How many written paths the summary of a group with an array describes one by one; it counts the rest. */
const PATHS_LISTED = 10;

/** Whether a result's group has an array: then its summary and its data fold each array's entries. */
export const hasArray = (r: Pick<ReplayResult, 'stateIds'>): boolean => r.stateIds.some((id) => ARRAY_STATE.test(id));

/** The port of a machine of an array, as the replay names it (`inputNames`): `sensors[3].raw`. */
const ARRAY_PORT = /^([^[\]/]+)\[(\d+)\]\.(.+)$/;

/** Names of ports in order, each port of an array's machines folded into one: `sensors[*].raw`, with the names it holds. */
function foldPorts<T>(items: readonly T[], nameOf: (item: T) => string): Array<{ name: string; array: string | null; items: T[] }> {
  const out: Array<{ name: string; array: string | null; items: T[] }> = [];
  const byKey = new Map<string, { name: string; array: string | null; items: T[] }>();
  for (const item of items) {
    const name = nameOf(item);
    const m = ARRAY_PORT.exec(name);
    if (!m) {
      out.push({ name, array: null, items: [item] });
      continue;
    }
    const key = `${m[1]}[*].${m[3]}`;
    const folded = byKey.get(key);
    if (folded) folded.items.push(item);
    else {
      const entry = { name: key, array: m[1], items: [item] };
      byKey.set(key, entry);
      out.push(entry);
    }
  }
  return out;
}

/** How many of an array's machines a folded port covers: `6 of 8 machines`, or `all 8 machines`. */
function portMachines(array: string, n: number, sizes: ReadonlyMap<string, number>): string {
  const size = sizes.get(array) ?? n;
  return n === size ? `all ${size} machines` : `${n} of ${size} machines`;
}

/**
 * Port names as the summaries and the data list them, with the ports of an array's machines folded:
 * `sensors[*].raw (all 8 machines)`. Other names stay as they are.
 */
export function portNamesText(names: readonly string[], stateIds: readonly string[]): string[] {
  const sizes = arraySizes(stateIds);
  return foldPorts(names, (n) => n).map((f) => (f.array ? `${f.name} (${portMachines(f.array, f.items.length, sizes)})` : f.name));
}

/**
 * The inputs set aside, as the summary and the data of a group with an array list them: the inputs of an
 * array's machines that were set aside for one reason are one entry, with the first one's path.
 */
export function skippedItems(
  skipped: readonly SkippedInput[],
  stateIds: readonly string[],
): Array<{ name: string | null; path: string; reason: string }> {
  const sizes = arraySizes(stateIds);
  return foldPorts(skipped, (s) => s.name ?? s.path).flatMap((f) => {
    if (!f.array) return f.items.map((s) => ({ name: s.name, path: s.path, reason: s.reason }));
    const byReason = new Map<string, SkippedInput[]>();
    for (const s of f.items) byReason.set(s.reason, [...(byReason.get(s.reason) ?? []), s]);
    return [...byReason].map(([reason, items]) => ({
      name: `${f.name} (${portMachines(f.array!, items.length, sizes)})`,
      path: items[0]!.path,
      reason,
    }));
  });
}

/** A configuration as the data of a group with an array gives it: each state of an array's machines folded, with how many machines. */
export function configurationItems(states: readonly string[], stateIds: readonly string[]): string[] {
  const sizes = arraySizes(stateIds);
  return foldStates(states.map((s) => [s, 1] as const)).map((f) => (f.array ? `${f.state} (${machinesOf(f, sizes)})` : f.state));
}

/** The counts of entries as the data of a group with an array gives them: each state of an array's machines folded, the counts added. */
export function visitedItems(visited: Readonly<Record<string, number>>): Record<string, number> {
  return Object.fromEntries(foldStates(Object.entries(visited)).map((f) => [f.state, f.n]));
}

/** The paragraph a person reads first and the AI tool returns. Plain facts,
 *  in the order someone asks about them, with the caveats attached to the
 *  numbers they qualify. */
export function summarize(r: ReplayResult): string {
  return joinSummary(summaryLines(r));
}

/** The facts of `summarize`, one line each, for the viewer. */
export function summaryLines(r: ReplayResult): SummaryLine[] {
  // A group with an array folds the entries of each array, and caps its lists: each machine
  // has its own input, its own states and its own PLC symbol. Any other model is summed up as before.
  const arrayed = hasArray(r);
  const sizes = arraySizes(r.stateIds);
  const iso = (ns: bigint) =>
    new Date(Number(ns / MS_NS))
      .toISOString()
      .replace('T', ' ')
      .replace(/\.\d+Z$/, 'Z');
  const minutes = Number((r.window.t1Ns - r.window.t0Ns) / MS_NS) / 60_000;
  const { lines, say, sayList } = summaryWriter();
  say(
    `Replayed ${iso(r.window.t0Ns)} to ${iso(r.window.t1Ns)} (${minutes % 1 === 0 ? minutes : minutes.toFixed(1)} min): ${count(r.rowCount)} recorded change(s) across ${r.inputs.length} input(s).`,
  );
  for (const n of r.notes) say(n.endsWith('.') ? n : `${n}.`, 'note');
  // Right after the count of inputs, because that count is what it corrects:
  // "across 3 input(s)" over a model with 5 connected inputs is the misreading.
  if (r.skipped.length) {
    // With an array, the inputs of its machines that were left out for one reason are one entry.
    const left = (arrayed ? skippedItems(r.skipped, r.stateIds) : r.skipped).map(
      (s) => `${s.name ?? s.path} (${s.reason.replace(/\.$/, '')})`,
    );
    sayList(
      `${r.skipped.length} of what it reads could not be replayed and ${r.skipped.length === 1 ? 'was' : 'were'} left out: `,
      left,
      '; ',
      '.',
    );
  }
  if (r.truncated.length)
    say(`History for ${r.truncated.join(', ')} was cut short by the reader's page cap, so the tail of the window is missing for it.`);
  const unseeded = r.inputs.filter((i) => i.seed === null);
  const unknownText = (i: ReplayInput): string =>
    `${i.name ?? i.path} had no value before the window started and was unknown ${i.unknownUntilNs === null ? 'for the whole window' : `until ${iso(i.unknownUntilNs)}`}`;
  if (unseeded.length) {
    // With an array, the inputs of its machines are one entry: how many had no value, and until when at the latest.
    const entries = arrayed
      ? foldPorts(unseeded, (i) => i.name ?? i.path).flatMap((f) => {
          if (!f.array) return f.items.map(unknownText);
          const whole = f.items.filter((i) => i.unknownUntilNs === null).length;
          const last = f.items.reduce<bigint | null>(
            (m, i) => (i.unknownUntilNs !== null && (m === null || i.unknownUntilNs > m) ? i.unknownUntilNs : m),
            null,
          );
          const until =
            last === null
              ? 'for the whole window'
              : `until ${iso(last)} at the latest${whole > 0 ? `, and ${whole} of them for the whole window` : ''}`;
          return [
            `${f.name} had no value before the window started (${portMachines(f.array, f.items.length, sizes)}) and was unknown ${until}`,
          ];
        })
      : unseeded.map(unknownText);
    sayList('', entries, '; ', '.');
  }
  // The states of an array's machines are folded (foldStates): `sensors/*/blocked`, and how many machines.
  say(`Started in ${configurationText(r.initialState, r.stateIds)} and ended in ${configurationText(r.finalState, r.stateIds)}.`);
  const entered = foldStates(Object.entries(r.visited)).map((f) =>
    f.array ? `${f.state} (${f.n}, in ${machinesOf(f, sizes)})` : `${f.state} (${f.n})`,
  );
  if (entered.length) sayList('Entered: ', entered, ', ', '.');
  if (r.neverEntered.length) sayList('Never entered: ', stateItems(r.neverEntered, r.stateIds), ', ', '.');
  const { moves, movesByEvent, writes, errors } = replayCounts(r.trace);
  const timers = movesByEvent.timer ?? 0;
  say(`${moves.length} state change(s) after boot${timers ? `, ${timers} of them fired by timers` : ''}.`);
  say(
    writes.length
      ? `${writes.length} write(s) captured and not sent: ${arrayed ? someOf([...new Set(writes.map((w) => w.path))], PATHS_LISTED) : [...new Set(writes.map((w) => w.path))].join(', ')}.`
      : 'No writes.',
  );
  // THE SENTENCE THAT PREVENTS THE STANDARD MISREADING. Without it a reader
  // sees "it opened the drain" and then a level that goes on rising, and
  // concludes the model or the replay is broken. Both are fine: the recording
  // is the plant as it actually ran, and it never received the decision.
  if (r.divergedAtNs !== null) {
    say(
      `Everything up to ${iso(r.divergedAtNs)} is exactly what this model would have decided, because until then it only watched. ` +
        `At ${iso(r.divergedAtNs)} it would have acted, and nothing received that — so the recorded values after it are the plant as it actually ran, not as this model would have left it.`,
    );
    // One sentence for each written path, in order. With an array: one for each path that changed, or could not
    // be compared, up to PATHS_LISTED, and one for all that never changed. Each machine writes its own symbol.
    const quiet = r.contrast.filter((c) => c.recorded !== null && c.recorded.length === 0).map((c) => c.symbolDecoded);
    const described = r.contrast.filter((c) => c.recorded === null || c.recorded.length > 0);
    for (const c of arrayed ? described.slice(0, PATHS_LISTED) : r.contrast) {
      if (c.recorded !== null && c.recorded.length === 0) {
        say(
          `${c.symbolDecoded} was never recorded changing in this window, so the plant did not do what the model would have asked.`,
          'written',
        );
      } else if (c.recorded === null) {
        say(`${c.symbolDecoded}: ${c.note}`, 'written');
      } else {
        const last = c.recorded[c.recorded.length - 1];
        say(
          `${c.symbolDecoded} actually recorded ${c.recorded.length} change(s) in this window, ending at ${String(last.value)}.`,
          'written',
        );
      }
    }
    if (arrayed && described.length > PATHS_LISTED)
      say(`${described.length - PATHS_LISTED} more written symbol(s) are not described here.`);
    if (arrayed && quiet.length > 0) {
      say(
        `${someOf(quiet, PATHS_LISTED)} ${quiet.length === 1 ? 'was' : 'were'} never recorded changing in this window, so the plant did not do what the model would have asked.`,
      );
    }
  }
  if (errors.length) say(`${errors.length} script error(s); the first: ${errors[0].message}.`);
  const slow = r.trace.filter((e) => e.kind === 'slow');
  if (slow.length) say(`${slow.length} step(s) took longer than the edge allows and would have been killed there.`);
  if (r.hung) say('The replay stopped responding and was killed; everything after that point is missing.');
  return lines;
}
